import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, Loader2, Feather, ImageDown, Send } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { renderPoemCard, sharePoemCard } from '@/utils/poemCard';
import { shareCardWithFriend, inviteLink } from '@/utils/shareCard';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from '@/hooks/useTelegram';

// ════════════════════════════════════════════════════════════════
// Стихотворение о пройденном Пути — подарок 21-го дня.
// Пишется один раз и остаётся навсегда.
//
// Имя поэта, в чьём стиле оно написано, человеку НЕ показываем:
// это подарок, а не упражнение в подражании.
// ════════════════════════════════════════════════════════════════

export default function PathPoem() {
  const nav = useNavigate();
  const { haptic } = useTelegram();
  const { profile } = useUserStore();

  const [poem, setPoem] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [cardUrl, setCardUrl] = useState<string | null>(null);

  async function saveCard() {
    if (!poem || saving) return;
    haptic('medium');
    setSaving(true);
    setError(null);
    try {
      const blob = await renderPoemCard(poem, { name: profile?.first_name });
      setCardUrl(URL.createObjectURL(blob));
      await sharePoemCard(blob);
      haptic('success');
    } catch {
      setError('Не получилось нарисовать картинку. Попробуй ещё раз.');
    } finally {
      setSaving(false);
    }
  }

  // Другу — той же картинкой, через Telegram (07.10.2026)
  async function sendToFriend() {
    if (!poem || !profile?.id || sending) return;
    haptic('medium');
    setSending(true);
    setError(null);
    try {
      const blob = await renderPoemCard(poem, { name: profile.first_name });
      await shareCardWithFriend(blob, {
        userId: profile.id,
        key: 'poem',
        caption: 'Стихотворение о моём Пути — написал Дофи в Slava Health',
        link: inviteLink((profile as any).ref_code),
        fileName: 'moy-put.png',
      });
    } catch {
      setError('Не получилось отправить. Попробуй ещё раз.');
    } finally {
      setSending(false);
    }
  }

  // Уже написанное показываем сразу, не трогая генерацию
  useEffect(() => {
    if (!profile?.id) return;
    (async () => {
      const { data } = await supabase
        .from('path_poems')
        .select('poem')
        .eq('user_id', profile.id)
        .maybeSingle();
      setPoem(data?.poem ?? null);
      setLoading(false);
    })();
  }, [profile?.id]);

  async function write() {
    if (writing) return;
    haptic('medium');
    setWriting(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('path-poem', { body: {} });
      if (fnErr) throw fnErr;
      if ((data as any)?.error) throw new Error(String((data as any).error));
      setPoem((data as any).poem);
      haptic('success');
    } catch (e: any) {
      setError(
        e?.message === 'path_not_completed'
          ? 'Стихотворение открывается, когда Путь пройден до конца.'
          : 'Не получилось. Попробуй ещё раз через минуту.',
      );
    } finally {
      setWriting(false);
    }
  }

  if (loading) {
    return (
      <div className="px-5 pb-28 max-w-md mx-auto pt-8 flex items-center gap-2 text-ink-dim text-sm">
        <Loader2 size={14} className="animate-spin" />
        Открываю…
      </div>
    );
  }

  return (
    <div className="px-5 pb-28 max-w-md mx-auto">
      <div className="sticky top-0 z-30 -mx-5 px-5 pt-4 pb-3 mb-6 backdrop-blur-xl bg-bg/75 border-b border-line/20 flex items-center gap-2">
        <button onClick={() => { haptic('light'); nav(-1); }} className="active:opacity-70">
          <ChevronLeft size={20} className="text-ink-muted" />
        </button>
        <div className="font-display text-lg">Твой путь в стихах</div>
      </div>

      {poem ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <div
            className="rounded-2xl border border-emerald-glow/25 p-6"
            style={{ background: 'linear-gradient(165deg, rgba(16,185,129,.10), transparent 70%)' }}
          >
            {/* whitespace-pre-line: строфы и переносы — часть стиха, их нельзя схлопывать */}
            <p className="whitespace-pre-line text-[15px] leading-[1.9] text-ink">{poem}</p>

            <div className="mt-6 pt-4 border-t border-line/50 text-[11px] text-ink-dim">
              {profile?.first_name ? `${profile.first_name}, ` : ''}написано по твоему Пути · Slava Health
            </div>
          </div>

          <button
            onClick={saveCard}
            disabled={saving}
            className="w-full mt-4 py-3.5 rounded-xl bg-bg-card border border-emerald-glow/40 font-medium active:scale-[0.99] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <ImageDown size={16} className="text-emerald-glow" />}
            {saving ? 'Рисую…' : 'Сохранить картинкой'}
          </button>
          <button
            onClick={sendToFriend}
            disabled={sending}
            className="w-full mt-2 py-3.5 rounded-xl bg-emerald-glow text-bg font-medium active:scale-[0.99] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {sending ? 'Готовлю…' : 'Отправить другу'}
          </button>

          {/* Предпросмотр: в мини-аппе скачивание срабатывает не всегда,
              а картинку на экране можно сохранить долгим нажатием */}
          {cardUrl && (
            <motion.img
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              src={cardUrl}
              alt="Карточка со стихотворением"
              className="w-full rounded-2xl border border-line mt-4"
            />
          )}

          <p className="text-[11px] text-ink-dim leading-relaxed mt-5 px-1">
            Это стихотворение написано один раз и остаётся с тобой. Его можно
            перечитать здесь в любой момент.
            {cardUrl && ' Картинку можно сохранить долгим нажатием.'}
          </p>
        </motion.div>
      ) : (
        <div className="text-center pt-6">
          <motion.div
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            className="w-16 h-16 rounded-full bg-emerald-glow/12 flex items-center justify-center mx-auto mb-5"
          >
            <Feather size={26} className="text-emerald-glow" />
          </motion.div>

          <h2 className="font-display text-xl leading-snug mb-2">Три недели за плечами</h2>
          <p className="text-sm text-ink-muted leading-relaxed mb-7">
            Дофи прочитал твой путь целиком — с чего ты начинал и к чему пришёл —
            и написал об этом стихотворение.
            <br />
            <span className="text-ink-dim">Оно пишется один раз.</span>
          </p>

          <button
            onClick={write}
            disabled={writing}
            className="w-full py-3.5 rounded-xl bg-emerald-glow text-bg font-semibold active:scale-[0.98] transition-transform disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {writing ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Дофи пишет…
              </>
            ) : (
              'Прочитать'
            )}
          </button>

          {error && <p className="text-[12px] text-danger mt-3">{error}</p>}
        </div>
      )}
    </div>
  );
}
